import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent } from "../../ui/dialog";
import { Drawer, DrawerContent } from "../../ui/drawer";
import { Calendar } from "../../ui/calendar";
import { Button } from "../../ui/button";
import { format } from "date-fns";
import useParking from "../../../api/parking";
import { CalendarRange, Check, X, Trash2, Calendar as CalendarIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../../context/NotificationContext';

const AddAvailabilityModal = ({ spot, userId, isOpen, onClose, onAddAvailability }) => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const [date, setDate] = useState({ from: null, to: null });
  const [month, setMonth] = useState(new Date());
  const [existingAvailability, setExistingAvailability] = useState([]);
  const [reservedDates, setReservedDates] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const parkingApi = useParking();

  useEffect(() => {
    // Check for mobile on mount and when window resizes
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  useEffect(() => {
    const fetchAvailability = async () => {
      if (spot && isOpen) { 
        try {
          const available = await parkingApi.getAvailableDates(spot.id);
          setExistingAvailability(available);
          
          const reserved = await parkingApi.getReservedDates(spot.id);
          setReservedDates(reserved);
        } catch (error) {
          console.error(t('parking.availability.errors.fetch'), error);
        }
      }
    };
    fetchAvailability();
  }, [spot, isOpen]);

  const handleAddAvailability = async () => {
    if (!spot || !spot.id || !date.from || !date.to) return;
    
    try {
      await onAddAvailability({
        spotId: spot.id,
        availableDates: { from: date.from, to: date.to }
      });
      setDate({ from: null, to: null }); // Reset date
      // Refresh availability list
      const available = await parkingApi.getAvailableDates(spot.id);
      setExistingAvailability(available);
      
      // Also refresh reserved dates
      const reserved = await parkingApi.getReservedDates(spot.id);
      setReservedDates(reserved);
      
      showNotification(t('parking.availability.success.add'), 'success');
    } catch (error) {
      console.error(t('parking.availability.errors.add'), error);
      showNotification(t('parking.availability.errors.add'), 'error');
    }
  };

  const checkAvailabilityHasReservations = (availability) => {
    if (!availability || !availability.start_date || !availability.end_date) {
      return false;
    }
    
    const availabilityStart = new Date(availability.start_date);
    const availabilityEnd = new Date(availability.end_date);
    
    return reservedDates.some(reservation => {
      // Skip cancelled reservations or invalid reservations
      if (!reservation || reservation.status === 'cancelled' || !reservation.start_time || !reservation.end_time) {
        return false;
      }
      
      const reservationStart = new Date(reservation.start_time);
      const reservationEnd = new Date(reservation.end_time);
      
      // Check if reservation overlaps with availability period
      return (
        reservationStart <= availabilityEnd && 
        reservationEnd >= availabilityStart
      );
    });
  };

  const handleDeleteAvailability = async (availabilityId) => {
    // Check if spot exists
    if (!spot || !spot.id) {
      console.error('Spot is null or missing id:', spot);
      showNotification(t('parking.availability.errors.delete'), 'error');
      return;
    }

    try {
      await parkingApi.deleteAvailability(availabilityId, userId);
      // Refresh availability list
      const available = await parkingApi.getAvailableDates(spot.id);
      setExistingAvailability(available);
      
      // Also refresh reserved dates
      const reserved = await parkingApi.getReservedDates(spot.id);
      setReservedDates(reserved);
      
      showNotification(t('parking.availability.success.delete'), 'success');
    } catch (error) {
      console.error(t('parking.availability.errors.delete'), error);
      
      // Check if it's a conflict error (reservations exist)
      if (error.response?.status === 400 || error.message?.includes('existing reservations')) {
        showNotification(t('parking.availability.errors.deleteConflict'), 'error');
      } else {
        showNotification(t('parking.availability.errors.delete'), 'error');
      }
    }
  };

  const isDateDisabled = (date) => {
    if (date < new Date()) return true;
    
    // Check if date is within any existing availability period
    return existingAvailability.some(period => {
      const start = new Date(period.start_date);
      const end = new Date(period.end_date);
      return date >= start && date <= end;
    });
  };

  // Handle date selection without resetting the month
  const handleDateSelect = (newDate) => {
    setDate(newDate);
    
    // Keep the calendar on the month of the latest selected date
    if (newDate?.from) {
      // If selecting the range start, don't change month yet
      if (!newDate.to && date.to) {
        // This is a new selection starting
        setMonth(newDate.from);
      }
    }
    
    // If selecting the end date, adjust visible month to show later date if needed
    if (newDate?.to) {
      setMonth(newDate.to);
    }
  };

  // Common content for both mobile and desktop
  const ModalContent = () => (
    <>
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <CalendarRange className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">{t('parking.availability.title')}</h2>
              <p className="text-sm text-muted-foreground">
                {t('parking.availability.subtitle')}
              </p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-6">
          {/* Daily Reward Info */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <span className="text-lg">💰</span>
              </div>
              <div>
                <h3 className="font-semibold text-green-800 dark:text-green-200">Daily Reward</h3>
                <p className="text-sm text-green-700 dark:text-green-300">
                  You'll receive <strong>10 points</strong> for each day your parking spot is available!
                </p>
              </div>
            </div>
          </div>

          {/* Mobile Layout */}
          <div className="lg:hidden space-y-6">
            {/* Calendar Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium flex items-center gap-2">
                <CalendarIcon className="h-5 w-5 text-primary" />
                Select Dates
              </h3>
              
              <Calendar
                mode="range"
                selected={date}
                onSelect={handleDateSelect}
                month={month}
                onMonthChange={setMonth}
                disabled={isDateDisabled}
                className="rounded-xl border border-neutral-200 dark:border-neutral-800 shadow-sm"
                modifiers={{
                  available: existingAvailability.map(period => ({
                    from: new Date(period.start_date),
                    to: new Date(period.end_date)
                  }))
                }}
                modifiersStyles={{
                  available: { 
                    backgroundColor: 'rgba(34, 197, 94, 0.1)',
                    color: '#16a34a',
                    pointerEvents: 'none'
                  }
                }}
                classNames={{
                  day_today: "bg-primary/10 text-primary font-bold",
                  day_selected: "bg-primary text-primary-foreground",
                  day_disabled: "text-muted-foreground opacity-50",
                  day_range_middle: "bg-primary/10",
                  day_hidden: "invisible",
                  button: "h-9 w-9 p-0 font-normal",
                  nav_button: "h-7 w-7 bg-transparent p-0",
                  caption: "text-sm font-medium",
                  caption_label: "text-sm font-medium",
                  head_cell: "text-muted-foreground text-xs font-medium",
                }}
              />

              {date.from && date.to && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400 font-medium mb-3">
                    <Check className="h-4 w-4" />
                    <span>Selected Period</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white dark:bg-neutral-800 p-3 rounded-lg">
                      <p className="text-xs text-muted-foreground mb-1">From</p>
                      <p className="font-semibold">{format(date.from, 'MMM d, yyyy')}</p>
                    </div>
                    <div className="bg-white dark:bg-neutral-800 p-3 rounded-lg">
                      <p className="text-xs text-muted-foreground mb-1">To</p>
                      <p className="font-semibold">{format(date.to, 'MMM d, yyyy')}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Existing Availability */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium flex items-center gap-2">
                <CalendarIcon className="h-5 w-5 text-primary" />
                Current Availability
              </h3>
              
              {existingAvailability.length === 0 ? (
                <div className="text-center py-8 bg-neutral-50 dark:bg-neutral-900 rounded-xl border border-dashed border-neutral-300 dark:border-neutral-700">
                  <CalendarIcon className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
                  <p className="text-muted-foreground">No availability set yet</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {existingAvailability.map((availability) => {
                    const hasReservations = checkAvailabilityHasReservations(availability);
                    return (
                      <div
                        key={availability.id}
                        className={`p-4 border rounded-xl ${
                          hasReservations 
                            ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800' 
                            : 'bg-neutral-50 dark:bg-neutral-900 border-neutral-200 dark:border-neutral-800'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <CalendarIcon className={`h-5 w-5 ${
                              hasReservations 
                                ? 'text-amber-600 dark:text-amber-400' 
                                : 'text-green-600 dark:text-green-400'
                            }`} />
                            <div>
                              <p className="font-medium text-sm">
                                {format(new Date(availability.start_date), 'MMM d')} - {format(new Date(availability.end_date), 'MMM d, yyyy')}
                              </p>
                              {hasReservations && (
                                <p className="text-xs text-amber-600 dark:text-amber-400">
                                  Has reservations
                                </p>
                              )}
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteAvailability(availability.id)}
                            disabled={hasReservations}
                            className={
                              hasReservations
                                ? "text-gray-400 cursor-not-allowed"
                                : "text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                            }
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden lg:grid lg:grid-cols-2 gap-8">
            {/* Left Column: Calendar */}
            <div className="space-y-6">
              <h3 className="text-lg font-medium flex items-center gap-2">
                <CalendarIcon className="h-5 w-5 text-primary" />
                Select Dates
              </h3>
              
              <Calendar
                mode="range"
                selected={date}
                onSelect={handleDateSelect}
                month={month}
                onMonthChange={setMonth}
                disabled={isDateDisabled}
                className="rounded-xl border border-neutral-200 dark:border-neutral-800 shadow-sm"
                modifiers={{
                  available: existingAvailability.map(period => ({
                    from: new Date(period.start_date),
                    to: new Date(period.end_date)
                  }))
                }}
                modifiersStyles={{
                  available: { 
                    backgroundColor: 'rgba(34, 197, 94, 0.1)',
                    color: '#16a34a',
                    pointerEvents: 'none'
                  }
                }}
                classNames={{
                  day_today: "bg-primary/10 text-primary font-bold",
                  day_selected: "bg-primary text-primary-foreground",
                  day_disabled: "text-muted-foreground opacity-50",
                  day_range_middle: "bg-primary/10",
                  day_hidden: "invisible",
                  button: "h-10 w-10 p-0 font-normal",
                  nav_button: "h-8 w-8 bg-transparent p-0",
                  caption: "text-base font-medium",
                  caption_label: "text-base font-medium",
                  head_cell: "text-muted-foreground text-sm font-medium",
                }}
              />

              {date.from && date.to && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400 font-medium mb-3">
                    <Check className="h-4 w-4" />
                    <span>Selected Period</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white dark:bg-neutral-800 p-3 rounded-lg">
                      <p className="text-sm text-muted-foreground mb-1">From</p>
                      <p className="font-semibold">{format(date.from, 'MMM d, yyyy')}</p>
                    </div>
                    <div className="bg-white dark:bg-neutral-800 p-3 rounded-lg">
                      <p className="text-sm text-muted-foreground mb-1">To</p>
                      <p className="font-semibold">{format(date.to, 'MMM d, yyyy')}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Right Column: Existing Availability */}
            <div className="space-y-6">
              <h3 className="text-lg font-medium flex items-center gap-2">
                <CalendarIcon className="h-5 w-5 text-primary" />
                Current Availability
              </h3>
              
              {existingAvailability.length === 0 ? (
                <div className="text-center py-12 bg-neutral-50 dark:bg-neutral-900 rounded-xl border border-dashed border-neutral-300 dark:border-neutral-700">
                  <CalendarIcon className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">No availability set yet</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {existingAvailability.map((availability) => {
                    const hasReservations = checkAvailabilityHasReservations(availability);
                    return (
                      <div
                        key={availability.id}
                        className={`p-4 border rounded-xl ${
                          hasReservations 
                            ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800' 
                            : 'bg-neutral-50 dark:bg-neutral-900 border-neutral-200 dark:border-neutral-800'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <CalendarIcon className={`h-5 w-5 ${
                              hasReservations 
                                ? 'text-amber-600 dark:text-amber-400' 
                                : 'text-green-600 dark:text-green-400'
                            }`} />
                            <div>
                              <p className="font-medium">
                                {format(new Date(availability.start_date), 'MMM d, yyyy')} - {format(new Date(availability.end_date), 'MMM d, yyyy')}
                              </p>
                              {hasReservations && (
                                <p className="text-sm text-amber-600 dark:text-amber-400">
                                  Has reservations
                                </p>
                              )}
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteAvailability(availability.id)}
                            disabled={hasReservations}
                            className={
                              hasReservations
                                ? "text-gray-400 cursor-not-allowed"
                                : "text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                            }
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="sticky bottom-0 bg-white dark:bg-neutral-900 border-t border-neutral-200 dark:border-neutral-800 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <Button 
            variant="outline" 
            onClick={onClose}
            className="flex-1 sm:flex-none"
          >
            {t('parking.availability.buttons.cancel')}
          </Button>
          <Button
            onClick={handleAddAvailability}
            disabled={!date.from || !date.to}
            className="flex-1 sm:flex-none bg-green-600 hover:bg-green-700 text-white"
          >
            {t('parking.availability.buttons.add')}
          </Button>
        </div>
      </div>
    </>
  );

  // Don't render modal if no spot is selected
  if (!spot && isOpen) {
    return null;
  }

  if (isMobile) {
    return (
      <Drawer open={isOpen} onOpenChange={onClose}>
        <DrawerContent className="max-h-[95vh]">
          <div className="flex flex-col h-[90vh]">
            <ModalContent />
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-[1000px] h-[90vh] p-0 overflow-hidden">
        <div className="flex flex-col h-full">
          <ModalContent />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddAvailabilityModal;